import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';
import { api } from '../api';
import { Logo } from '../components/Logo';
import { formatElapsed } from '../format';

interface PublicResults {
  race: { name: string; raceDate: string; status: string; final: boolean };
  points: { id: string; name: string; km: number; kind: string }[];
  rows: { place: number | null; bib: string | null; name: string; division: string | null; divisionPlace: number | null; splits: Record<string, number | null>; gunMs: number | null; netMs: number | null }[];
}

/** Results anyone can open from a link or QR code; refreshes during the race. */
export function PublicResultsPage() {
  const { raceId = '' } = useParams();
  const [q, setQ] = useState('');
  const [division, setDivision] = useState('');
  const res = useQuery({
    queryKey: ['public-results', raceId],
    queryFn: () => api<PublicResults>(`/public/races/${raceId}/results`, { auth: 'none' }),
    refetchInterval: (query) => (query.state.data?.race.final ? false : 30_000),
    retry: false,
  });

  if (res.error) {
    return (
      <div className="center-page">
        <div className="card center-card stack">
          <h1 className="display" style={{ fontSize: 24 }}>
            Results not available
          </h1>
          <p className="muted" style={{ margin: 0 }}>
            The race organiser hasn’t published results for this race yet.
          </p>
        </div>
      </div>
    );
  }
  if (!res.data) return <div className="center-page">Loading results…</div>;

  const { race, points, rows } = res.data;
  const splits = points.filter((p) => p.kind !== 'finish');
  const divisions = [...new Set(rows.map((r) => r.division).filter((d): d is string => !!d))].sort();
  const needle = q.trim().toLowerCase();
  const shown = rows.filter((r) => (!division || r.division === division) && (!needle || `${r.bib ?? ''} ${r.name}`.toLowerCase().includes(needle)));
  const hasNet = rows.some((r) => r.netMs !== null);

  return (
    <>
      <header className="bar">
        <div className="bar-inner">
          <span className="brand">
            <Logo />
            <span>Splitline</span>
          </span>
        </div>
      </header>
      <main className="page" style={{ gap: 16 }}>
        <section className="page-head">
          <div>
            <h1>{race.name}</h1>
            <p>
              {race.raceDate} · {rows.filter((r) => r.gunMs !== null).length} finishers
            </p>
          </div>
        </section>
        {!race.final && (
          <div className="notice warn">
            Provisional results: times may still change while the race is in progress and the timing team checks crossings. This page updates every 30 seconds.
          </div>
        )}
        <div className="inline">
          <input className="input" type="search" placeholder="Find a runner by name or bib" aria-label="Find a runner" value={q} onChange={(e) => setQ(e.target.value)} style={{ flex: '1 1 220px' }} />
          {divisions.length > 0 && (
            <select className="input" value={division} onChange={(e) => setDivision(e.target.value)} aria-label="Division" style={{ flex: '0 1 200px' }}>
              <option value="">All divisions</option>
              {divisions.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          )}
        </div>
        <section className="card flush">
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>{division ? 'Div.' : 'Place'}</th>
                  <th>Runner</th>
                  {splits.map((p) => (
                    <th key={p.id} className="num">
                      {p.name}
                    </th>
                  ))}
                  <th className="num">Time</th>
                  {hasNet && <th className="num">Net</th>}
                </tr>
              </thead>
              <tbody>
                {shown.map((r, i) => (
                  <tr key={`${r.bib}-${i}`}>
                    <td className="mono">{(division ? r.divisionPlace : r.place) ?? '—'}</td>
                    <td>
                      <b>{r.name}</b>
                      <div className="small muted">
                        {r.bib ? `#${r.bib}` : ''}
                        {r.division ? ` · ${r.division}${r.divisionPlace ? ` (${r.divisionPlace})` : ''}` : ''}
                      </div>
                    </td>
                    {splits.map((p) => (
                      <td key={p.id} className="num mono">
                        {r.splits[p.id] != null ? formatElapsed(r.splits[p.id]!, 1) : '—'}
                      </td>
                    ))}
                    <td className="num mono">
                      <b>{r.gunMs !== null ? formatElapsed(r.gunMs, 1) : '—'}</b>
                    </td>
                    {hasNet && <td className="num mono">{r.netMs !== null ? formatElapsed(r.netMs, 1) : '—'}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {shown.length === 0 && <p className="empty">No runners match.</p>}
        </section>
        <p className="small muted" style={{ margin: 0 }}>
          Timed with Splitline. Times are gun times{hasNet ? '; net times start when the runner crossed the start line' : ''}.
        </p>
      </main>
    </>
  );
}
